import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'

/**
 * The one net under the whole tree.
 *
 * Every screen that fetches its own data already turns a failed request into an `Alert`
 * through `QueryState` -- this catches what none of those can: a render itself throwing
 * (a bad prop shape, `undefined` where a response is assumed present, a bug). React
 * unmounts the tree below whichever boundary catches, so without one anywhere the result
 * is a blank white page with no way back short of a manual reload. This is that boundary,
 * mounted once around the router in `main.tsx`, not per screen -- one bad render should
 * not have to be anticipated by every screen individually.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center gap-4 p-8">
          <Alert tone="error" title="Something went wrong">
            The screen hit a problem it could not recover from. Reloading usually fixes it;
            if it keeps happening, tell whoever runs this system what you were doing.
          </Alert>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      )
    }

    return this.props.children
  }
}
